// useNoticeView — one message as localized copy + tone, per notice variant
// (where and how long it shows is messages.ts). A hook (not a pure fn)
// because the copy uses the lingui `t` macro; the undo variants carry an
// action wired to the restore flow passed in as deps.

import { exhaustSwitchCase } from "@bilda/utils";
import { plural } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { Button } from "../../components/Button/Button.js";
import {
    type TourView,
    useViewTours,
} from "../../components/GuidedTour/viewTours.js";
import type { ResourceRef } from "../../components/Navigation/types.js";
import type { ToastTone } from "../../components/Toast/Toast.js";
import type { TurnFailure } from "../../modules/chat.js";
import type { HostNotice, SaveRetry } from "../frame.js";
import type { PortResult } from "../ports/result.js";

type NoticeView = {
    readonly title: string;
    readonly description: string;
    readonly tone: ToastTone;
    readonly action?: ReactNode;
} | null;

export type NoticeViewDeps = {
    readonly undoDelete: (ref: ResourceRef, priorContentHash: string) => void;
    readonly undoDeleteConversation: (conversationId: string) => void;
    // Undo for "conversation-archived".
    readonly unarchiveConversationNow: (conversationId: string) => void;
    // Retry for "save-failed".
    readonly retrySave: (retry: SaveRetry) => void;
    // Undo for "module-made" / "course-made": delete the new container.
    readonly deleteResourceNow: (ref: ResourceRef) => void;
    // Undo for "parts-added": take the parts back out of the module.
    readonly removeFromModule: (
        moduleId: string,
        parts: readonly string[],
    ) => void;
    // Undo for "part-moved": the part back to its old place.
    readonly movePartBack: (
        moduleId: string,
        part: string,
        index: number,
    ) => void;
    // Undo for "part-removed": the part in again, at its old place.
    readonly putPartBack: (moduleId: string, part: string, at: number) => void;
    // Undo for "removed-from-canvas": the cards back on the canvas.
    readonly putBackOnCanvas: (ids: readonly string[]) => void;
    // "Keep mine" / "Take theirs" on "edit-conflict".
    readonly keepMine: (materialId: string, elementId: string) => void;
    readonly takeTheirs: (materialId: string, elementId: string) => void;
    // Undo for "project-archived": the project back from the archive (the
    // Library's live refresh shows it).
    readonly restoreProject: (projectId: string) => PortResult<void>;
    // "Continue" after a restore: sends `text` as the author's message in
    // that conversation, re-driving its agent (the host's `submitText`).
    readonly sendPrompt: (text: string, conversationId: string) => void;
    // Localizes a chat turn failure.
    readonly turnFailureText: (failure: TurnFailure) => string;
    // "Show" on "sources-kept": opens Uploaded sources.
    readonly showUploadedSources: () => void;
    // "Take the tour" on "tour-offered": starts that view's guided tour.
    readonly startTour: (view: TourView) => void;
};

export const useNoticeView = (
    notice: HostNotice | null,
    {
        undoDelete,
        undoDeleteConversation,
        unarchiveConversationNow,
        retrySave,
        deleteResourceNow,
        removeFromModule,
        movePartBack,
        putPartBack,
        putBackOnCanvas,
        restoreProject,
        sendPrompt,
        turnFailureText,
        showUploadedSources,
        startTour,
        keepMine,
        takeTheirs,
    }: NoticeViewDeps,
): NoticeView => {
    const { t } = useLingui();
    const tours = useViewTours();
    if (notice === null) return null;
    const n = notice;
    switch (n.type) {
        case "attach":
            return {
                title: t`Attach`,
                description: t`Attaching files is mocked in this preview.`,
                tone: "neutral",
            };
        case "reference":
            return {
                title: t`Reference`,
                description: t`Inserting @-references is mocked in this preview.`,
                tone: "neutral",
            };
        case "media":
            return {
                title: t`Media`,
                description: t`Adding media to the editor isn't available yet.`,
                tone: "neutral",
            };
        case "edit-failed":
            return {
                title: t`Edit not saved`,
                description: t`The server couldn't save that change. Your text was reverted — try again.`,
                tone: "danger",
            };
        case "edit-conflict":
            return {
                title: t`Changed elsewhere.`,
                description: t`This text was changed while you were writing. Your version is kept until you choose.`,
                tone: "warning",
                action: (
                    <>
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => keepMine(n.materialId, n.elementId)}
                        >
                            {t`Keep mine`}
                        </Button>
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() =>
                                takeTheirs(n.materialId, n.elementId)
                            }
                        >
                            {t`Take theirs`}
                        </Button>
                    </>
                ),
            };
        case "suggestion-outdated":
            return {
                title: t`You changed this text after the suggestion was made.`,
                description: t`Your text is kept. Ask the assistant again for a suggestion on it.`,
                tone: "warning",
            };
        case "undo-refused":
            return {
                title: t`Can't undo: this changed elsewhere.`,
                description: t`Nothing was overwritten.`,
                tone: "warning",
            };
        case "redo-refused":
            return {
                title: t`Can't redo: this changed elsewhere.`,
                description: t`Nothing was overwritten.`,
                tone: "warning",
            };
        case "turn-failed":
            return {
                title: t`No reply`,
                description: turnFailureText(n.failure),
                tone: "danger",
            };
        case "ingest-failed":
            return {
                title: t`Couldn't add source`,
                description: `${n.filename} — ${n.message}`,
                tone: "danger",
            };
        case "source-deduped":
            if (n.onCanvas && n.existingTitle !== undefined)
                return {
                    title: t`“${n.existingTitle}” is already on this canvas – shown.`,
                    description: "",
                    tone: "neutral",
                };
            return {
                title: t`Already have this file`,
                description:
                    n.existingTitle !== undefined
                        ? t`The file already existed as "${n.existingTitle}" — using it.`
                        : t`This file already exists in your workspace — using the existing source.`,
                tone: "neutral",
            };
        case "resource-deleted":
            return {
                title: t`Deleted “${n.ref.title}”`,
                description: t`Undo brings it back exactly as it was.`,
                tone: "neutral",
                action: (
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => undoDelete(n.ref, n.priorContentHash)}
                    >
                        {t`Undo`}
                    </Button>
                ),
            };
        case "conversation-deleted":
            return {
                title: t`Deleted “${n.title}”`,
                description: t`The whole thread is kept — Undo brings it back.`,
                tone: "neutral",
                action: (
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => undoDeleteConversation(n.conversationId)}
                    >
                        {t`Undo`}
                    </Button>
                ),
            };
        case "module-made":
            return {
                title:
                    n.count === 0
                        ? t`Made a new module`
                        : t`${plural(n.count, {
                              one: "Put # card in a new module",
                              other: "Put # cards in a new module",
                          })}`,
                description: "",
                tone: "success",
                action: (
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => deleteResourceNow(n.ref)}
                    >
                        {t`Undo`}
                    </Button>
                ),
            };
        case "course-made":
            return {
                title:
                    n.count === 0
                        ? t`Made a new course`
                        : t`${plural(n.count, {
                              one: "Put # card in a new course",
                              other: "Put # cards in a new course",
                          })}`,
                description: "",
                tone: "success",
                action: (
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => deleteResourceNow(n.ref)}
                    >
                        {t`Undo`}
                    </Button>
                ),
            };
        case "parts-added":
            return {
                title:
                    n.parts.length === 1 && n.number > 0
                        ? t`Added as part ${n.number}`
                        : t`${plural(n.parts.length, {
                              one: "Added # part",
                              other: "Added # parts",
                          })}`,
                description: "",
                tone: "success",
                action: (
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => removeFromModule(n.moduleId, n.parts)}
                    >
                        {t`Undo`}
                    </Button>
                ),
            };
        case "part-moved":
            return {
                title: t`Moved to place ${n.to + 1}`,
                description: "",
                tone: "success",
                action: (
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => movePartBack(n.moduleId, n.part, n.from)}
                    >
                        {t`Undo`}
                    </Button>
                ),
            };
        case "part-removed":
            return {
                title:
                    n.kind === "course"
                        ? t`Removed from the course`
                        : t`Removed from the module`,
                // Short, for the canvas's one-line toast: the part stays on
                // the canvas, where the author sees it.
                description: "",
                tone: "success",
                action: (
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => putPartBack(n.moduleId, n.part, n.at)}
                    >
                        {t`Undo`}
                    </Button>
                ),
            };
        // The label at the drop spot (shown at the object, not as a toast).
        case "already-part":
            return {
                title: t`Already part ${n.number} – shown`,
                description: "",
                tone: "neutral",
            };
        case "removed-from-canvas":
            return {
                title:
                    n.ids.length === 1
                        ? t`Removed “${n.title}” from the canvas`
                        : t`${plural(n.ids.length, {
                              one: "Removed # card from the canvas",
                              other: "Removed # cards from the canvas",
                          })}`,
                description: "",
                tone: "success",
                action: (
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => putBackOnCanvas(n.ids)}
                    >
                        {t`Undo`}
                    </Button>
                ),
            };
        case "lifecycle-failed":
            return {
                title: t`That didn't work`,
                description: n.message,
                tone: "danger",
            };
        case "project-archived":
            return {
                title: t`Archived “${n.title}”`,
                description: "",
                tone: "neutral",
                action: (
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => restoreProject(n.projectId)}
                    >
                        {t`Undo`}
                    </Button>
                ),
            };
        case "sources-kept": {
            const [first] = n.titles;
            return {
                title:
                    n.titles.length === 1 && first !== undefined
                        ? t`${first} is kept in your uploaded sources.`
                        : t`${n.titles.length} sources are kept in your uploaded sources.`,
                description: "",
                tone: "neutral",
                action: (
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={showUploadedSources}
                    >
                        {t`Show`}
                    </Button>
                ),
            };
        }
        case "tour-offered":
            return {
                title: t`A short tour shows how ${tours[n.view].name} works.`,
                description: "",
                tone: "neutral",
                action: (
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => startTour(n.view)}
                    >
                        {t`Take the tour`}
                    </Button>
                ),
            };
        case "conversation-archived":
            return {
                title: t`Archived “${n.title}”`,
                description: "",
                tone: "neutral",
                action: (
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={() =>
                            unarchiveConversationNow(n.conversationId)
                        }
                    >
                        {t`Undo`}
                    </Button>
                ),
            };
        case "save-failed": {
            const { retry } = n;
            return {
                title: t`Couldn't save`,
                description: n.message,
                tone: "danger",
                action: (
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => retrySave(retry)}
                    >
                        {t`Retry`}
                    </Button>
                ),
            };
        }
        case "copied":
            return {
                title: t`Copied: ${n.label}`,
                description: "",
                tone: "success",
            };
        case "feedback-sent":
            return {
                title: t`Thanks, your report was sent.`,
                description: "",
                tone: "success",
            };
        case "copy-failed":
            return {
                title: t`Couldn't copy`,
                description: t`Select the text and copy it yourself.`,
                tone: "warning",
            };
        case "source-restored": {
            const { conversationId } = n;
            return {
                title:
                    n.sourceTitle !== undefined
                        ? t`Restored “${n.sourceTitle}”`
                        : t`Source restored`,
                description: t`Its content is back. References and citations still point to it.`,
                tone: "success",
                action:
                    conversationId === undefined ? undefined : (
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() =>
                                sendPrompt(t`Continue`, conversationId)
                            }
                        >
                            {t`Continue`}
                        </Button>
                    ),
            };
        }
        case "exported":
            return {
                title: t`Downloaded ${n.filename}`,
                description: t`Saved to your Downloads folder.`,
                tone: "success",
            };
        // Dev-command feedback (DX doc §5). Plain English, never localized —
        // these variants only exist under `import.meta.env.DEV`. `dev-started`
        // is the < 100ms acknowledgment; the completion notice replaces it.
        case "dev-started":
            return {
                title: n.label,
                description: "Working…",
                tone: "neutral",
            };
        case "dev-succeeded":
            return {
                title: n.label,
                description: `${n.durationMs} ms`,
                tone: "success",
            };
        case "dev-failed":
            return {
                title: n.label,
                description: n.message,
                tone: "danger",
            };
        case "replay-degraded": {
            // The calm user-facing half of ADR-3 degradation. Counts only;
            // reassures nothing was lost (preserve-not-drop).
            const parts = [
                n.frozen > 0
                    ? t`${n.frozen} item(s) shown with limited detail`
                    : undefined,
                n.unroutable > 0
                    ? t`${n.unroutable} change(s) couldn't be shown`
                    : undefined,
            ].filter((p): p is string => p !== undefined);
            return {
                title: t`Some history loaded partially`,
                description: `${parts.join(" · ")}. ${t`Nothing was lost — the rest of your workspace is unaffected.`}`,
                tone: "warning",
            };
        }
        default:
            return exhaustSwitchCase(n);
    }
};
